import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Mail, Plus, Trash2, Edit3, Copy, Send, Settings, FileDown, Palette, PenTool } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import EmailTemplateBuilder from './EmailTemplateBuilder';
import EmailTemplatePreview from './EmailTemplatePreview';
import { formatDateForUI } from '@/lib/core/time-formatting';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { CanvaImportDialog } from './CanvaImportDialog';
import { CanvaTemplateMappingDialog } from './CanvaTemplateMappingDialog';
import { parseCanvaHtml, type TextBlock, type Button as ParsedButton } from '@/lib/email/canva-html-parser';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Pass generator server URL
const PASS_GENERATOR_URL = import.meta.env.VITE_PASS_GENERATOR_URL || 'https://pass.taginn.app';
// Canva App ID from the Canva app configuration
const CANVA_APP_ID = 'AAG44bVUzMY';

interface EmailTemplate {
  id: string;
  name: string;
  configuration: {
    preset?: string;
    logoUrl?: string;
    logoPosition?: 'left' | 'center' | 'right';
    primaryColor?: string;
    fontFamily?: string;
    headerText?: string;
    mainMessage?: string;
    ctaText?: string;
    ctaLink?: string;
    buttonType?: 'review' | 'coupon' | 'loyalty' | 'custom' | 'none';
    selectedReviewId?: string;
    selectedCouponId?: string;
    selectedLoyaltyId?: string;
    footerText?: string;
    html?: string;
    importSource?: 'canva' | 'canva_app' | 'custom';
    // Canva App import fields
    canvaHeaderImage?: string;  // URL to uploaded Canva design image
    canvaBlocks?: Array<{
      id: string;
      type: 'editable_text' | 'cta_button';
      content?: string;
      label?: string;
      buttonType?: string;
      ctaUrl?: string;
    }>;
  };
  user_id: string;
  created_at: string;
  updated_at: string;
}

interface EmailTemplateManagerProps {
  businessId: string | null;
}

const EmailTemplateManager: React.FC<EmailTemplateManagerProps> = ({ businessId }) => {
  const { user } = useAuth();
  const { setBusinessDashboardTab } = useDashboardTab();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCanvaImportOpen, setIsCanvaImportOpen] = useState(false);
  const [editingCanvaTemplate, setEditingCanvaTemplate] = useState<EmailTemplate | null>(null);
  const [showCanvaEditor, setShowCanvaEditor] = useState(false);
  const [canvaTextBlocks, setCanvaTextBlocks] = useState<TextBlock[]>([]);
  const [canvaButtons, setCanvaButtons] = useState<ParsedButton[]>([]);
  const [canvaHtmlPreview, setCanvaHtmlPreview] = useState('');
  const [showSettingsConfirmDialog, setShowSettingsConfirmDialog] = useState(false);
  const [showCreateChoiceDialog, setShowCreateChoiceDialog] = useState(false);
  const [isOpeningCanva, setIsOpeningCanva] = useState(false);

  // localStorage key for builder state
  const BUILDER_STATE_KEY = `emailTemplateBuilderState_${user?.id}`;

  // Load builder state from localStorage
  const loadBuilderState = () => {
    try {
      const saved = localStorage.getItem(BUILDER_STATE_KEY);
      return saved ? JSON.parse(saved) : { showBuilder: false, editingTemplateId: null };
    } catch (error) {
      console.error('Error loading builder state:', error);
      return { showBuilder: false, editingTemplateId: null };
    }
  };

  const builderState = loadBuilderState();

  const [showBuilder, setShowBuilder] = useState(false); // Always start with false, restore after templates load
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [hasRestoredState, setHasRestoredState] = useState(false); // Track if we've restored from localStorage

  // Masonry layout state
  const [cardHeights, setCardHeights] = useState<Record<string, number>>({});
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Measure card heights for masonry layout
  const measureCards = useCallback(() => {
    const heights: Record<string, number> = {};
    Object.entries(cardRefs.current).forEach(([id, el]) => {
      if (el) {
        heights[id] = el.offsetHeight;
      }
    });
    setCardHeights(heights);
  }, []);

  // Re-measure when templates change or after initial render
  useEffect(() => {
    // Measure after a short delay to allow iframes to load
    const timer1 = setTimeout(measureCards, 100);
    const timer2 = setTimeout(measureCards, 500);
    const timer3 = setTimeout(measureCards, 1000);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [templates, measureCards]);

  // Distribute items into columns (left-to-right, shortest column first)
  const getMasonryColumns = useCallback(() => {
    const allItems = [...templates.map(t => ({ type: 'template' as const, data: t })), { type: 'add' as const, data: null }];
    const columns: Array<typeof allItems> = [[], []];
    const columnHeights = [0, 0];

    allItems.forEach((item) => {
      const id = item.type === 'template' ? item.data!.id : 'add-card';
      const height = cardHeights[id] || 300; // Default height estimate

      // Add to shorter column
      const shorterColumn = columnHeights[0] <= columnHeights[1] ? 0 : 1;
      columns[shorterColumn].push(item);
      columnHeights[shorterColumn] += height + 24; // 24px gap
    });

    return columns;
  }, [templates, cardHeights]);

  // Save builder state to localStorage - but ONLY after initial restoration
  useEffect(() => {
    if (!hasRestoredState) return; // Don't save until we've restored

    const state = {
      showBuilder,
      editingTemplateId: editingTemplate?.id || null,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(BUILDER_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving builder state:', error);
    }
  }, [showBuilder, editingTemplate, BUILDER_STATE_KEY, hasRestoredState]);

  // Restore editing template and builder state AFTER templates have loaded
  useEffect(() => {
    if (hasRestoredState) return; // Only restore once

    if (templates.length > 0) {
      if (builderState.editingTemplateId) {
        const templateToEdit = templates.find(t => t.id === builderState.editingTemplateId);
        if (templateToEdit) {
          setEditingTemplate(templateToEdit);
          // Only show builder after editing template is restored
          if (builderState.showBuilder) {
            setShowBuilder(true);
          }
        }
      } else if (builderState.showBuilder) {
        // User was creating a new template (no editingTemplateId)
        setShowBuilder(true);
      }

      // Mark that we've completed the restoration
      setHasRestoredState(true);
    }
  }, [templates, builderState.editingTemplateId, builderState.showBuilder, showBuilder, hasRestoredState]);

  useEffect(() => {
    if (user?.id) {
      fetchTemplates();
    }
  }, [user?.id]);

  const fetchTemplates = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    // Show choice dialog instead of directly opening builder
    setShowCreateChoiceDialog(true);
  };

  const handleCreateInTagInn = () => {
    setShowCreateChoiceDialog(false);
    setEditingTemplate(null);
    setShowBuilder(true);
  };

  const handleCreateInCanva = () => {
    // Open Canva to create a new design
    // Note: Deep linking to specific design types with our app requires a released app
    // For now, we open Canva's design creation page and instruct user to find the app
    //
    // Once the app is released, we can use:
    // https://www.canva.com/login/?redirect=%2Fdesign%3Fcreate%26type%3D<TYPE-ID>%26ui%3D<APP-UI>
    // where ui is the app's unique identifier (found in preview URL after ui= param)
    const canvaUrl = 'https://www.canva.com/create/email-newsletters/';

    window.open(canvaUrl, '_blank');

    setShowCreateChoiceDialog(false);
    toast.success('Opening Canva', {
      description: 'Create an email design, then go to Apps → search "TagInn" → log in with your TagInn account to add CTA buttons.',
      duration: 10000,
    });
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    // Check if it's a Canva template - show inline editor instead of dialog
    const importSource = template.configuration?.importSource;
    if (importSource === 'canva' || importSource === 'canva_app') {
      // Parse the HTML to get text blocks and buttons
      const html = template.configuration?.html || '';
      const parsed = parseCanvaHtml(html);

      setCanvaTextBlocks(parsed.textBlocks);
      setCanvaButtons(parsed.buttons);
      setCanvaHtmlPreview(html);
      setEditingCanvaTemplate(template);
      setShowCanvaEditor(true);
      return;
    }

    setEditingTemplate(template);
    setShowBuilder(true);
  };

  const handleCanvaEditorComplete = () => {
    setShowCanvaEditor(false);
    setEditingCanvaTemplate(null);
    setCanvaTextBlocks([]);
    setCanvaButtons([]);
    setCanvaHtmlPreview('');
    fetchTemplates(); // Refresh the list
  };

  const handleCanvaEditorCancel = () => {
    setShowCanvaEditor(false);
    setEditingCanvaTemplate(null);
    setCanvaTextBlocks([]);
    setCanvaButtons([]);
    setCanvaHtmlPreview('');
  };

  const handleBuilderComplete = () => {
    setShowBuilder(false);
    setEditingTemplate(null);
    // Clear builder state from localStorage
    try {
      localStorage.removeItem(BUILDER_STATE_KEY);
    } catch (error) {
      console.error('Error clearing builder state:', error);
    }
    fetchTemplates(); // Refresh the list
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this email template? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      toast.success('Email template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting email template:', error);
      toast.error('Failed to delete email template');
    }
  };

  const duplicateTemplate = async (template: EmailTemplate) => {
    try {
      const { data: newTemplate, error } = await supabase
        .from('email_templates')
        .insert({
          user_id: template.user_id,
          name: `${template.name} (Copy)`,
          configuration: template.configuration
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Template duplicated successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const getPresetDisplayName = (presetId?: string) => {
    const presets: { [key: string]: string } = {
      'welcome': 'Welcome Email',
      'loyalty': 'Loyalty',
      'coupon-reward': 'Coupon Reward',
      'event-invitation': 'Event Invitation',
      'newsletter': 'Newsletter',
      'promotion': 'Promotion'
    };
    return presets[presetId || ''] || 'Custom';
  };

  const shouldShowButton = (config: any) => {
    return config?.buttonType && config.buttonType !== 'none';
  };

  const getButtonText = (config: any) => {
    // Use custom text if provided
    if (config?.ctaText && config.ctaText.trim()) {
      return config.ctaText;
    }

    // Fallback to button type
    switch (config?.buttonType) {
      case 'review':
        return 'Leave Review';
      case 'coupon':
        return 'Get Coupon';
      case 'loyalty':
        return 'Join Program';
      case 'custom':
        return 'Learn More';
      default:
        return 'Button';
    }
  };

  const getButtonUrl = (config: any) => {
    return '#'; // Preview only, no real URL needed
  };

  const renderTemplatePreview = (config: any) => {
    // Use card mode for any template with HTML (to allow full-width rendering)
    const hasHtml = !!config.html;

    return (
      <EmailTemplatePreview
        configuration={config}
        mode={hasHtml ? "card" : "compact"}
        isAdvancedMode={false} // Don't use advanced mode styling for cards
        shouldShowButton={shouldShowButton}
        getButtonText={getButtonText}
        getButtonUrl={getButtonUrl}
      />
    );
  };

  if (loading) {
    return <div>Loading email templates...</div>;
  }

  // Show builder if in creation or editing mode
  if (showBuilder) {
    return (
      <EmailTemplateBuilder
        businessId={businessId}
        existingTemplate={editingTemplate}
        onComplete={handleBuilderComplete}
        onCancel={() => {
          setShowBuilder(false);
          setEditingTemplate(null);
          // Clear builder state from localStorage
          try {
            localStorage.removeItem(BUILDER_STATE_KEY);
          } catch (error) {
            console.error('Error clearing builder state:', error);
          }
        }}
      />
    );
  }

  // Show Canva editor if editing a Canva template
  if (showCanvaEditor && businessId) {
    return (
      <CanvaTemplateMappingDialog
        inline={true}
        textBlocks={canvaTextBlocks}
        buttons={canvaButtons}
        htmlPreview={canvaHtmlPreview}
        businessId={businessId}
        onSaved={handleCanvaEditorComplete}
        onCancel={handleCanvaEditorCancel}
        editingTemplate={editingCanvaTemplate ? {
          id: editingCanvaTemplate.id,
          name: editingCanvaTemplate.name,
          configuration: editingCanvaTemplate.configuration
        } : undefined}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Email Templates</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowSettingsConfirmDialog(true)}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Configure Sender
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsCanvaImportOpen(true)}
            className="flex items-center gap-2"
          >
            <FileDown className="h-4 w-4" />
            Import from Canva
          </Button>
        </div>
      </div>

      <div className="w-full space-y-6">
        {templates.length === 0 ? (
          <div className="columns-1 lg:columns-2 gap-6">
            {/* Create Template Card - Empty State */}
            <Card
              className="overflow-hidden border-2 border-dashed border-muted-foreground/30 hover:border-primary hover:shadow-lg transition-all duration-200 break-inside-avoid mb-6 cursor-pointer bg-muted/30"
              onClick={handleCreateTemplate}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-muted rounded-lg">
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-muted-foreground">Create Your First Template</CardTitle>
                      <CardDescription className="text-xs text-muted-foreground">
                        Start building email campaigns
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0 pb-0">
                {/* Status Badge Placeholder */}
                <div className="flex items-center justify-between">
                  <div className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    New Template
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Click to create
                  </div>
                </div>
              </CardContent>

              {/* Plus Icon where preview would be - outside CardContent to match real cards */}
              <div className="border-t-2 border-dashed border-muted-foreground/30 flex items-center justify-center py-20">
                <Plus className="h-16 w-16 text-muted-foreground/40" />
              </div>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Masonry layout with two columns */}
            <div className="flex gap-6">
              {getMasonryColumns().map((column, colIndex) => (
                <div key={colIndex} className="flex-1 flex flex-col gap-6">
                  {column.map((item) => {
                    if (item.type === 'template') {
                      const template = item.data!;
                      return (
                        <div
                          key={template.id}
                          ref={(el) => { cardRefs.current[template.id] = el; }}
                        >
                          <Card className="overflow-hidden border hover:shadow-lg transition-shadow duration-200">
                            <CardHeader className="pb-3">
                              <div className="flex justify-between items-start">
                                <div className="flex items-center gap-2">
                                  <div className="p-2 bg-primary/10 dark:bg-primary/20 rounded-lg text-primary dark:text-primary">
                                    <Mail className="h-4 w-4" />
                                  </div>
                                  <div>
                                    <CardTitle className="text-base font-semibold">{template.name}</CardTitle>
                                    <CardDescription className="text-xs">
                                      {getPresetDisplayName(template.configuration.preset)}
                                    </CardDescription>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditTemplate(template)}
                                    className="h-8 w-8 p-0 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 border hover:border-blue-400"
                                    title="Edit template"
                                  >
                                    <Edit3 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => duplicateTemplate(template)}
                                    className="h-8 w-8 p-0 text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 border hover:border-green-400"
                                    title="Duplicate template"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteTemplate(template.id)}
                                    className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border hover:border-red-400"
                                    title="Delete template"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardHeader>

                            <CardContent className="pt-0 pb-0">
                              {/* Template Info */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="text-xs text-muted-foreground">
                                    Created {formatDateForUI(template.created_at)}
                                  </div>
                                  {(template.configuration.importSource === 'canva' || template.configuration.importSource === 'canva_app') && (
                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                                      {template.configuration.importSource === 'canva_app' ? 'From Canva App' : 'Imported from Canva'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </CardContent>

                            {/* Template Preview - outside CardContent to avoid padding */}
                            {renderTemplatePreview(template.configuration)}
                          </Card>
                        </div>
                      );
                    } else {
                      // Add Template Card
                      return (
                        <div
                          key="add-card"
                          ref={(el) => { cardRefs.current['add-card'] = el; }}
                        >
                          <Card
                            className="overflow-hidden border-2 border-dashed border-muted-foreground/30 hover:border-primary hover:shadow-lg transition-all duration-200 cursor-pointer bg-muted/30"
                            onClick={handleCreateTemplate}
                          >
                            <CardHeader className="pb-3">
                              <div className="flex justify-between items-start">
                                <div className="flex items-center gap-2">
                                  <div className="p-2 bg-muted rounded-lg">
                                    <Plus className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                  <div>
                                    <CardTitle className="text-base font-semibold text-muted-foreground">Add Email Template</CardTitle>
                                    <CardDescription className="text-xs text-muted-foreground">
                                      Create new template
                                    </CardDescription>
                                  </div>
                                </div>
                              </div>
                            </CardHeader>

                            <CardContent className="pt-0 pb-0">
                              {/* Status Badge Placeholder */}
                              <div className="flex items-center justify-between">
                                <div className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                                  New Template
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Click to create
                                </div>
                              </div>
                            </CardContent>

                            {/* Plus Icon where preview would be - outside CardContent to match real cards */}
                            <div className="border-t-2 border-dashed border-muted-foreground/30 flex items-center justify-center py-20">
                              <Plus className="h-16 w-16 text-muted-foreground/40" />
                            </div>
                          </Card>
                        </div>
                      );
                    }
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Canva Import Dialog */}
      <CanvaImportDialog
        open={isCanvaImportOpen}
        onClose={() => {
          setIsCanvaImportOpen(false);
          setEditingCanvaTemplate(null);
        }}
        onSaved={() => {
          setIsCanvaImportOpen(false);
          setEditingCanvaTemplate(null);
          fetchTemplates(); // Refresh template list after saving
        }}
        onZipExtracted={(html, textBlocks, buttons) => {
          // Close import dialog and show inline editor
          setIsCanvaImportOpen(false);
          setCanvaHtmlPreview(html);
          setCanvaTextBlocks(textBlocks);
          setCanvaButtons(buttons);
          setEditingCanvaTemplate(null); // New template, not editing
          setShowCanvaEditor(true);
        }}
        businessId={businessId || undefined}
        editingTemplate={editingCanvaTemplate}
      />

      {/* Create Template Choice Dialog */}
      <Dialog open={showCreateChoiceDialog} onOpenChange={setShowCreateChoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Email Template</DialogTitle>
            <DialogDescription>
              Choose how you want to create your email template
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* TagInn Builder Option */}
            <button
              onClick={handleCreateInTagInn}
              className="flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 transition-all duration-200 group"
            >
              <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                <PenTool className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">In TagInn</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use our simple builder with templates
                </p>
              </div>
            </button>

            {/* Canva Option */}
            <button
              onClick={handleCreateInCanva}
              disabled={isOpeningCanva}
              className="flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                {isOpeningCanva ? (
                  <div className="h-8 w-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Palette className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                )}
              </div>
              <div className="text-center">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">In Canva</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isOpeningCanva ? 'Opening Canva...' : 'Design with full creative freedom'}
                </p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Navigation Confirmation Dialog */}
      <AlertDialog open={showSettingsConfirmDialog} onOpenChange={setShowSettingsConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Go to Settings?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will take you to the <strong>Settings tab</strong> where you can set your sender name and reply-to email.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // Set a flag in sessionStorage to trigger email sender highlighting
                sessionStorage.setItem('highlightEmailSender', 'true');
                setBusinessDashboardTab('settings');
                setShowSettingsConfirmDialog(false);
              }}
            >
              Go to Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailTemplateManager;
